import React, { FC, ReactNode, useEffect, useRef, useState } from "react";
import { styled } from "@mui/system";
import {
  COLORS,
  FONTS,
  FONT_FAMILIES,
  FONT_WEIGHTS,
  RADII,
  SIZES,
  SPACINGS,
} from "../../common/consts";
import { makeShouldForwardProps } from "../../common/utils/ui";
import Text, { TextVariant } from "./Text";
import Spinner from "./Spinner";

interface SaveButtonWrapperProps {
  loading?: boolean;
}

const shouldForwardProp = makeShouldForwardProps<SaveButtonWrapperProps>([
  "loading",
]);

const SaveButtonWrapper = styled("div", {
  shouldForwardProp,
})<SaveButtonWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SaveButtonStyled = styled("button")<{ loading?: boolean }>`
  position: relative;
  background-color: ${({ loading }) =>
    loading ? COLORS.saveButtonDisabledBg : COLORS.saveButtonPrimary};
  color: ${({ loading }) =>
    loading ? COLORS.saveButtonDisabledText : COLORS.foregroundWhite};
  border: ${({ loading }) =>
    loading
      ? `1px solid var(--color-saveButtonDisabledBorder, ${COLORS.saveButtonDisabledBorder})`
      : "none"};
  border-radius: ${SIZES.saveButtonBorderRadius};
  height: ${SIZES.saveButtonHeight};
  padding: 0 ${SPACINGS.lg};
  margin: ${SPACINGS.none};
  min-width: fit-content;
  box-sizing: border-box;
  font-size: ${SPACINGS.sm};
  font-weight: 575;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  cursor: ${({ loading }) => (loading ? "not-allowed" : "pointer")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  font-family: ${FONT_FAMILIES.regular};
  transition: background-color 0.2s ease;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    background-color: ${({ loading }) =>
      loading ? COLORS.saveButtonDisabledBg : COLORS.saveButtonHover};
  }

  &:disabled {
    background-color: var(--color-saveButtonDisabledBg, ${COLORS.saveButtonDisabledBg}) !important;
    color: var(--color-saveButtonDisabledText, ${COLORS.saveButtonDisabledText}) !important;
    border: 1px solid var(--color-saveButtonDisabledBorder, ${COLORS.saveButtonDisabledBorder}) !important;
    cursor: not-allowed;
  }
`;

const NativeButton = styled("button")`
  opacity: 0;
  pointer-events: none;
  margin: 0;
`;

export interface SaveButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  loading?: boolean;
  onSave?: () => void | Promise<void>;
  children?: ReactNode;
  nativeButtonRef?: React.RefObject<HTMLButtonElement>;
}

const SaveButton: FC<SaveButtonProps> = ({
  loading = false,
  onSave,
  children = "Save",
  nativeButtonRef,
  disabled,
  className = "",
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(loading);
  const internalNativeRef = useRef<HTMLButtonElement>(null);
  const customButtonRef = useRef<HTMLButtonElement>(null);
  const nativeRef = nativeButtonRef || internalNativeRef;

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || isLoading) {
      e.preventDefault();
      return;
    }

    // If it's a form submit button, let it submit naturally
    // The loading state is controlled by the parent component
    if (rest.type === "submit" && !onSave) {
      // Form will submit naturally, loading state managed by parent
      return;
    }

    // For custom onSave handler, prevent default and handle manually
    if (onSave) {
      e.preventDefault();
      e.stopPropagation();
      setIsLoading(true);

      try {
        await onSave();
      } catch (error) {
        console.error("Save button error:", error);
      } finally {
        // Reset loading state after a delay if not controlled
        if (!loading) {
          setTimeout(() => {
            setIsLoading(false);
          }, 2000);
        }
      }
    } else if (nativeRef.current) {
      e.preventDefault();
      e.stopPropagation();
      setIsLoading(true);
      nativeRef.current.click();
      // Reset after delay if not controlled
      if (!loading) {
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
      }
    }
  };

  return (
    <SaveButtonWrapper loading={isLoading} className={className}>
      <SaveButtonStyled
        className="boring-save-button"
        ref={customButtonRef}
        type={rest.type || "button"}
        loading={isLoading}
        disabled={disabled || isLoading}
        onClick={handleClick}
        style={rest.style}
        {...rest}
      >
        {/* Keep children in DOM but hide when loading to maintain button size */}
        <span style={{ 
          visibility: isLoading ? "hidden" : "visible",
          display: "inline-flex",
          alignItems: "center",
        }}>
          {typeof children === "string" ? (
            <Text variant={TextVariant.sm} style={{ color: "inherit", fontWeight: 575 }}>
              {children}
            </Text>
          ) : (
            children
          )}
        </span>
        {/* Overlay spinner when loading */}
        {isLoading && (
          <span style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <Spinner />
          </span>
        )}
      </SaveButtonStyled>
    </SaveButtonWrapper>
  );
};

export default SaveButton;

